<template>
  <Card class="communication-setting-wrapper" headless>
    <div class="communication-setting">
      <div class="communication-setting__details">
        <div class="communication-setting__name">
          {{ communicationSetting.name }}
          <span v-if="isRequired()">- Required</span>
          <Tooltip v-if="isRequired()" placement="top">
            <template #trigger>
              <i
                class="fas fa-info-circle ml-1"
                tabindex="0"
                role="button"
                aria-label="More information"
              ></i>
            </template>
            <template #tooltip>
              <span>
                This is a critical system communication. It cannot be turned
                off.
              </span>
            </template>
          </Tooltip>
          <Tooltip v-if="infoContent" placement="top" :width="340">
            <template #trigger>
              <i
                class="fas fa-info-circle ml-1"
                tabindex="0"
                role="button"
                aria-label="How this notification works"
              ></i>
            </template>
            <template #tooltip>
              <div class="communication-setting__info">
                <div class="communication-setting__info-heading">
                  {{ infoContent.heading }}
                </div>
                <div
                  v-for="section in infoContent.sections"
                  :key="section.title"
                  class="communication-setting__info-section"
                >
                  <strong>{{ section.title }}</strong>
                  <div
                    v-for="(item, index) in section.items"
                    :key="index"
                    style="display: flex; gap: 6px; margin-top: 3px"
                  >
                    <span>&bull;</span>
                    <span>{{ item }}</span>
                  </div>
                </div>
              </div>
            </template>
          </Tooltip>
        </div>
        <div class="communication-setting__description">
          {{ communicationSetting.description }}
        </div>
      </div>
      <div class="communication-setting__toggles">
        <div
          v-for="format in communicationSetting.supported_formats"
          :key="format"
          class="format-setting"
        >
          <div class="format-setting__icon-wrapper">
            <i :class="formatConfig[format].icon"></i>
          </div>
          <div>{{ formatConfig[format].name }}</div>
          <div class="format-setting__switch">
            <Tooltip :disabled="!isDisabled(format)">
              <template #trigger>
                <ToggleSwitch
                  :modelValue="displayValue(format)"
                  :disabled="isDisabled(format)"
                   @update:modelValue="updateValue(format, $event)"
                />
              </template>
              <template #tooltip>
                <span>{{ disabledTooltip(format) }}</span>
              </template>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import Card from "@/components/Card.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import Tooltip from "@/components/Tooltip.vue";

const formatConfig = {
  email: { name: "Email", icon: "fa-regular fa-envelope" },
  sms: { name: "Text", icon: "fa-regular fa-message-lines" },
  push: { name: "App notification", icon: "fa-regular fa-message-exclamation" }
};

// Delivery-behavior tooltips, keyed by "<category>_<key>" (the notification's
// full identifier) so a same-named key in another category can't inherit this
// copy. Documentation-only; lives on the frontend. Scoped to the appointment
// reminder per TIDE-1772; extend here as the pattern rolls out.
const INFO_TOOLTIPS = {
  customer_appointment_reminder: {
    heading: "How this notification works",
    sections: [
      {
        title: "Timing",
        items: [
          "Sent 24 hours before the appointment start time.",
          "Not sent if the appointment is scheduled less than 24 hours in advance.",
          "If the appointment is rescheduled, the reminder shifts to 24 hours before the new appointment time.",
          "If the appointment is cancelled, the pending reminder is not sent."
        ]
      },
      {
        title: "Delivery window",
        items: [
          "Email is sent at the scheduled time.",
          "SMS is only sent between 8:00 AM and 9:00 PM in the customer's local time to avoid inconvenient hours. If a reminder would land outside that window, it's held until the next 8:00 AM in their local time."
        ]
      },
      {
        title: "Opt-outs",
        items: [
          "Respects the customer's SMS and email opt-out settings on their profile.",
          "Can be toggled off per customer under Customer > Communications."
        ]
      }
    ]
  }
};

const props = defineProps({
  communicationSetting: {
    type: Object,
    required: true
  },
  communicationSettings: {
    type: Object,
    required: true
  }
});

const currentValues = ref({
  sms: props.communicationSetting.sms,
  email: props.communicationSetting.email,
  push: props.communicationSetting.push
});

const emit = defineEmits(["change"]);

const infoContent = computed(() => {
  const { category, key } = props.communicationSetting;
  return INFO_TOOLTIPS[`${category}_${key}`] ?? null;
});

function updateValue(key, value) {
  currentValues.value[key] = value;
  emit("change", {
    category: props.communicationSetting.category,
    key: props.communicationSetting.key,
    enabled: isEnabled(),
    ...currentValues.value
  });
}

function isEnabled() {
  return props.communicationSetting.supported_formats.some((format) => {
    return currentValues.value[format];
  });
}

function isRequired() {
  return props.communicationSetting.required;
}

function isDisabled(format) {
  return props.communicationSettings.isDisabled({
    category: props.communicationSetting.category,
    key: props.communicationSetting.key,
    format
  });
}

function displayValue(format) {
  return props.communicationSettings.displayValue({
    category: props.communicationSetting.category,
    key: props.communicationSetting.key,
    format
  });
}

function disabledTooltip(format) {
  if (displayValue(format)) {
    return "Required";
  } else {
    return "Disabled by a higher level setting";
  }
}
</script>

<style lang="scss" scoped>
.communication-setting-wrapper {
  margin-bottom: 10px;
}
.communication-setting {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  &--required {
    margin-top: 13px;
  }

  &__note {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    background: #ffec99;
    padding: 4px 21px;
  }

  &__name {
    font-weight: 700;
  }

  &__info {
    text-align: left;
    font-weight: 400;
  }

  &__info-heading {
    font-weight: 700;
    margin-bottom: 8px;
  }

  &__info-section {
    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }

  &__toggles {
    width: 250px;
  }
}
.format-setting {
  display: flex;
  align-items: center;
  padding-bottom: 10px;

  &:last-of-type {
    padding-bottom: 0px;
  }

  &__icon-wrapper {
    display: flex;
    width: 24px;
    font-size: 16px;
  }

  &__switch {
    margin-left: auto;
  }
}
</style>
